import { sharedTestIds } from '#utils/sharedTestIds'

export const testIds = {
  ...sharedTestIds,

  setup: {
    page: 'setup.page',
    startConfiguration: 'setup.start-configuration',
    startConfigTitle: 'setup.start-config-title',
    headerExistingConfigurations: 'setup.header-existing-configurations',
    userProfile: 'setup.user-profile',
    marketSelect: 'setup.market-select',
    marketSelectButton: <T1 extends string = string, T2 extends string = string>(countryCode: T1, languageCode: T2) =>
      `setup.market-select-button.${countryCode}.${languageCode}` as const,
    startConfigurationButton: 'setup.start-configuration-button',
    lastCode: 'setup.last-code',
    lastCodeClear: 'setup.last-code-clear',
    manCodeInput: 'setup.man-code-input',
    modeChooser: 'setup.mode-chooser',
    loadButton: 'setup.load-button',
    searchParam: <T extends string = string>(key: T) => `setup.search-param.${key}` as const,
    callingParameters: {
      title: 'setup.calling-parameters.title',
      parameters: 'setup.calling-parameters.parameters',
      returnUrl: 'setup.calling-parameters.return-url',
      returnUrlValue: 'setup.calling-parameters.return-url-display',
      parameterDisplay: 'setup.calling-parameters.parameter-display',
      language: 'setup.calling-parameters.language',
      marketCheckbox: 'setup.calling-parameters.market-checkbox',
      market: 'setup.calling-parameters.market',
      configurationType: 'setup.calling-parameters.configuration-type',
      vehicleType: 'setup.calling-parameters.vehicle-type',
      view: 'setup.calling-parameters.view',
      input: 'setup.calling-parameters.input',
      callingUrl: 'setup.calling-parameters.calling-url',
    },
  },

  exit: {
    header: 'exit.header',
    confirm: 'exit.confirm',
    cancel: 'exit.cancel',
    unsavedModal: 'exit.unsaved-modal',
    pendingModal: 'exit.pending-modal',
  },

  header: {
    statusIcon: 'header.status-icon',
    createConfiguration: 'header.create-configuration',
    dropdownMenu: 'header.dropdown-menu',
    dropdownEntry: {
      makeLeading: 'header.dropdown-entry.make-leading',
      cloneConfiguration: 'header.dropdown-entry.clone-configuration',
      showManCode: 'header.dropdown-entry.show-man-code',
      changeBaseParameters: 'header.dropdown-entry.change-base-parameters',
      printConfiguration: 'header.dropdown-entry.print-configuration',
      export: 'header.dropdown-entry.export',
      compare: 'header.dropdown-entry.compare',
      rename: 'header.dropdown-entry.rename',
      close: 'header.dropdown-entry.close',
      configurationInformation: 'header.dropdown-entry.configuration-information',
    },
    buttons: {
      filterPrintButton: 'header.buttons.filter-print-button',
    },
  },

  configuration: {
    navigationTab: <T extends string = string>(configId: T) => `configuration.navigation.tab.${configId}` as const,
    header: {
      dropdown: 'configuration.header.dropdown',
      title: 'configuration.header.title',
      titleInput: 'configuration.header.titleInput',
    },
    convertToTmsButton: 'configuration.convert-to-tms-button',
    ecm: { warningToast: 'configuration.ecm.warning-toast', injectStatus: 'configuration.ecm.inject-status' },
    error: {
      page: 'configuration.error.page',
      close: 'configuration.error.close',
      retry: 'configuration.error.retry',
    },
    informationOverlay: 'configuration.information-overlay',
  },

  landing: {
    page: 'landing.page',
    referenceTrucks: 'landing.reference-trucks',
    modelBased: 'landing.model-based',
    missionSpecific: 'landing.mission-specific',
    manCode: {
      element: 'landing.man-code.element',
      input: 'landing.man-code.input',
      button: 'landing.man-code.button',
    },
  },

  general: {
    page: 'general.page',
    groupTitle: 'general.group-title',
    familyTitle: 'general.family-title',
    detailedConfigurationButton: 'general.detailed-configuration-button',
  },

  details: {
    page: 'details.page' as const,
    header: 'details.header' as const,
    table: 'details.table' as const,

    navigation: {
      generalStep: 'details.navigation.general-step',
      detailedStep: 'details.navigation.detailed-step',
    },
    optionOverlay: 'details.option-overlay',

    row: <T extends string = string>(path: T) => `details.row.${path}` as const,
    variantRowCloser: 'details.row.variant-closer',
    optionCell: {
      element: 'details.option-cell.element',
      checkIcon: 'details.option-cell.check-icon',
      value: 'details.option-cell.value',
      revert: 'details.option-cell.revert',
      lock: 'details.option-cell.lock',
    },
    variantCellTitle: <T extends string = string>(path: T) => `details.variant-cell-title.${path}` as const,
    familyFilter: <T extends string = string>(path: T) => `details.family-filter.${path}` as const,
    currentPrice: <T extends string = string>(path: T) => `details.current-price.${path}` as const,
    rowFavoriteButton: 'details.row.favorite-button',
    rowFamilyName: 'details.row.family-name',
    toggleCollapseEntryButton: 'details.toggle-collapse-entry-button',
    groupFavoriteCell: 'details.group-favorite-cell',
    optionRowLockIcon: 'details.option-row-lock-icon',
    action: {
      showDetails: 'details.action.show-details',
    },
    familyDeltaPriceRangeGraph: 'details.family-delta-price-range-graph',
    familyDeltaPriceRangeGraphTooltip: 'details.family-delta-price-range-graph-tooltip',
    variantDeltaPriceRangeGraph: 'details.variant-delta-price-range-graph',
    togglePrintColumnsButton: 'details.toggle-print-columns-button',
    familyDescription: 'details.family-description',
    cosimaDescription: 'details.cosima-description',
    cosimaShortDescription: 'details.cosima-short-description',
    cosimaBenefits: 'details.cosima-benefits',
    cosimaTechnicalData: 'details.cosima-technical-data',
    cosimaFunctions: 'details.cosima-functions',
    collapseIcon: 'details.collapse-icon',
    partPeriodButton: 'details.part-period-button',
    changeDelta: 'details.change-delta',
    historyEntry: 'details.history-entry',
    headerCollapse: 'details.header-collapse',
  },

  print: {
    modal: 'print.modal',
    showOptionsButton: 'print.show-options-button',
    toggleHideInPrint: 'print.toggle-hide-in-print',
    toggleEmphasizeInPrint: 'print.toggle-emphasize-in-print',
    toggleDescriptionInPrint: 'print.toggle-description-in-print',
    toggleImageInPrint: 'print.toggle-image-in-print',
    dropdown: {
      elements: 'print.dropdown.elements',
      toggleEmphasizeInPrint: 'print.dropdown.toggle-emphasize-in-print',
      toggleDescriptionInPrint: 'print.dropdown.toggle-description-in-print',
    },
    filterBar: {
      viewModes: 'print.filter-bar.view-modes',
    },
    row: <T extends string = string>(path: T) => `print.row.${path}` as const,
    close: 'print.close',
  },

  comparison: {
    modal: 'comparison.modal',
    swap: 'comparison.swap',
    chip: 'comparison.chip',
    close: 'comparison.close',
    selectionButton: 'comparison.selection-button',
    actions: {
      element: 'comparison.actions.element',
      apply: 'comparison.actions.apply',
      cancel: 'comparison.actions.cancel',
    },
    status: {
      differences: 'comparison.status.differences',
      noDifferences: 'comparison.status.no-differences',
      selectConfiguration: 'comparison.status.select-configuration',
      mismatchingStep: 'comparison.status.mismatching-step',
      loading: 'comparison.status.loading',
    },
    header: 'comparison.header',
    headerTitle: 'comparison.header-title',
    comparisonHeader: 'comparison.comparison-title',
    comparisonDetails: 'comparison.comparison-details',
    baseHeader: 'comparison.base-title',
    baseDetails: 'comparison.base-details',
    modeSelector: 'comparison.mode-selector',
    table: 'comparison.table',

    sidebarTree: {
      element: 'comparison.sidebar-tree.element',
      subgroup: 'comparison.sidebar-tree.subgroup',
      subgroupCount: 'comparison.sidebar-tree.subgroup-count',
      group: 'comparison.sidebar-tree.group',
    },
    noDifferences: 'comparison.no-differences',
    selectConfiguration: 'comparison.select-configuration',
    row: <T extends string = string>(id: T) => `comparison.row.${id}` as const,
    changeCount: <T extends string = string>(id: T) => `comparison.change-count.${id}` as const,
    configSelector: {
      element: 'comparison.config-selector.element',
      current: 'comparison.config-selector.current',
      recent: 'comparison.config-selector.recent',
      references: 'comparison.config-selector.references',
      loadCode: {
        tab: 'comparison.config-selector.load-code.tab',
        input: 'comparison.config-selector.load-code.input',
        button: 'comparison.config-selector.load-code.button',
      },
    },
  },

  optionalEquipment: {
    page: 'optional-equipment.page',
    sidebar: 'optional-equipment.sidebar',
    noOptions: 'optional-equipment.no-options',
    create: 'optional-equipment.create',
    titleInput: 'optional-equipment.title-input',
    descriptionInput: 'optional-equipment.description-input',
    detailsCreate: 'optional-equipment.details-create',
    remove: 'optional-equipment.remove',
    duplicate: 'optional-equipment.duplicate',
    checkOffer: 'optional-equipment.check-offer',
    saveImplicit: 'optional-equipment.save-implicit',
    dropdown: {
      element: 'optional-equipment.dropdown.element',
      offer: 'optional-equipment.dropdown.offer',
      equipmentRow: 'optional-equipment.dropdown.equipment-row',
    },
  },

  accordion: {
    title: 'accordion.title',
    marketDenomination: 'accordion.market-denomination',
    details: {
      entry: 'accordion.details.entry',
      entryName: 'accordion.details.entry-name',
      emptyEntry: 'accordion.details.entry-undefined',
    },
    history: {
      undo: 'accordion.history.undo',
      redo: 'accordion.history.redo',
    },
  },

  history: {
    page: 'history.page',
    entryTitle: 'history.entry-title',
    navigationChip: 'history.navigation-chip',
    navigationChipTooltip: 'history.navigation-chip.tooltip',
    sidebarEntry: {
      entry: 'history.sidebar-entry.entry',
      family: 'history.sidebar-entry.family',
      variant: 'history.sidebar-entry.variant',
    },
    closeView: 'history.close-view',
    viewMode: 'history.view-mode',
    accordionEntry: 'history.accordion-entry',
    list: {
      element: 'history.list.element',
      selectedChange: 'history.list.selected-change',
      variantChanges: 'history.list.variant-changes',
      obsoleteChanges: 'history.list.obsolete-changes',
      change: <T extends string = string>(familyId: T) => `history.list.change.${familyId}` as const,
      addition: <T extends string = string>(familyId: T) => `history.list.addition.${familyId}` as const,
      obsolete: <T extends string = string>(familyId: T) => `history.list.obsolete.${familyId}` as const,
    },
    price: {
      previous: 'history.price-previous',
      delta: 'history.price-delta',
      sum: 'history.price-sum',
    },
    changeRow: {
      familyId: 'history.change-row.family-id',
      familyTitle: 'history.change-row.family-title',
      previousVariant: 'history.change-row.previous-variant',
      selectionPrev: 'history.change-row.selection-prev',
      selectionPost: 'history.change-row.selection-post',
      currentVariant: 'history.change-row.current-variant',
      priceDifference: 'history.change-row.price-difference',
    },
    conflict: {
      element: 'history.conflict.element',
      accept: 'history.conflict.accept',
      revert: 'history.conflict.reject',
    },
    showList: 'history.show-list',
    showTable: 'history.show-table',
    table: 'history.table',
  },

  summary: {
    page: 'summary.page',
    redirectPage: 'summary.redirect-page',
    redirectNowButton: 'summary.redirect-now-button',
    entry: 'summary.entry',
    overviewEntry: <T extends string = string>(id: T) => `summary.overview-entry.${id}` as const,
    codeArea: {
      configurationActionButtons: 'summary.code-area.configuration-action-buttons',
      readOnlyWarning: 'summary.code-area.read-only-warning',
      finalizeButton: 'summary.code-area.finalize-button',
      submitButton: 'summary.submit-button',
      cancelButton: 'summary.cancel-button',
    },
    viewToggle: {
      element: 'summary.view-toggle.element',
      result: 'summary.view-toggle.result',
      changes: 'summary.view-toggle.changes',
    },
    views: {
      result: 'summary.views.result',
      changes: 'summary.views.changes',
    },
  },

  changeBaseParameters: {
    overlay: 'change-base-parameters.overlay',
    overridesGrid: 'change-base-parameters.overrides-grid',
    deliveryDateInput: 'change-base-parameters.delivery-date-input',
    languageInput: 'change-base-parameters.language-input',
    advancedButton: 'change-base-parameters.advanced-button',
    preview: 'change-base-parameters.preview',
  },

  manCode: {
    header: {
      element: 'man-code.header.element',
      modeChooser: 'man-code.header.mode-chooser',
      loadButton: 'man-code.header.load-button',
    },
    table: 'man-code.table',
    tableEntry: <T extends string | number = string | number>(identifier: T) => `man-code.table-entry.${identifier}` as const,
    saveButton: 'man-code.save-button',
    saveToast: 'man-code.save-toast',
    saveButtonSpinner: 'man-code.save-button-spinner',
    revertButton: 'man-code.revert-button',
    copyField: 'man-code.copy-field',
    emptyField: 'man-code.empty-field',
    makeLeading: 'man-code.make-leading',
    infoButton: 'man-code.info-button',
    informationOverlay: {
      element: 'man-code.information-overlay.element',
    },
  },

  restartModal: {
    element: 'restart-modal.element',
    manCodeInput: 'restart-modal.man-code-input',
    configModeSelect: 'restart-modal.config-mode-select',
    close: 'restart-modal.close',
    manCodeStartButton: 'restart-modal.man-code-start-button',
  },
  copyModal: {
    closeButton: 'copy-modal.close-button',
    copyButton: 'copy-modal.copy-button',
    expirySegmentedButton: {
      element: 'copy-modal.expiry-segmented-button',
      fiveMin: 'copy-modal.expiry-segmented-button.5min',
      oneMin: 'copy-modal.expiry-segmented-button.1min',
      immediate: 'copy-modal.expiry-segmented-button.immediate',
    },
  },

  technicalData: {
    page: 'technical-data.page',
    button: 'technical-data.button',
    closeView: 'technical-data.close-view',
    group: {
      co2: 'technical-data.group.co2',
      dimensions: 'technical-data.group.dimensions',
    },
    subgroup: {
      lengths: 'technical-data.subgroup.lengths',
      widths: 'technical-data.subgroup.widths',
      heights: 'technical-data.subgroup.heights',
      frame: 'technical-data.subgroup.frame',
      payloads: 'technical-data.subgroup.payloads',
      loads: 'technical-data.subgroup.loads',
      angles: 'technical-data.subgroup.angles',
      maneuverability: 'technical-data.subgroup.maneuverability',
      turningCircles: 'technical-data.subgroup.turning-circles',
      n3g: 'technical-data.subgroup.n3g',
      n3gList: 'technical-data.subgroup.n3g-list',
      co2Information: 'technical-data.subgroup.co2-information',
      co2Predictions: 'technical-data.subgroup.co2-predictions',
    },
    entries: {
      chassisWeightWithCab: 'technical-data.entry.chassis-weight-with-cab',
      frontChassisWeight: 'technical-data.entry.front-chassis-weight',
      rearChassisWeight: 'technical-data.entry.rear-chassis-weight',
      totalLength: 'technical-data.entry.total-length',
      frontVehicleOverhang: 'technical-data.entry.front-vehicle-overhang',
    },
    detailEntryMarkdown: <T extends string = string>(testId: T) => `${testId}.markdown` as const,
  },

  buildVerification: {
    page: 'build-verification.page',
    header: 'build-verification.header',
    retryButton: 'build-verification.retry-button',
    statusChip: <T extends string = string>(status: T) => `build-verification.status-chip.${status}` as const,
    task: <T extends string = string>(taskId: T) => `build-verification.task.${taskId}` as const,
    entry: <T extends string = string>(label: T) => `build-verification.entry.${label}` as const,
    entrySection: <T extends string = string>(label: T) => `build-verification.entry-section.${label}` as const,
    progressBar: 'build-verification.progress-bar',
    icon: 'build-verification.icon',
    sidebar: 'build-verification.sidebar',
    sidebarButton: 'build-verification.sidebar-button',
    sidebarTask: <T extends string = string>(taskId: T) => `build-verification.sidebar-task.${taskId}` as const,
    signal: <T extends string = string>(status: T) => `build-verification.signal.${status}` as const,
  },

  journal: {
    versionEntry: 'journal.version-entry',
    details: 'journal.details',
    collapseFpcChanges: 'journal.collapse-fpc-changes',
    entries: {
      fpc: 'journal.entries.fpc',
      other: 'journal.entries.other',
      history: 'journal.entries.history',
    },
    state: {
      empty: 'journal.state.empty',
    },
  },
} as const
